import type { PropsWithChildren, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';

import { getL10nNumber } from '@travel/i18n';
import { Sustainability } from '@travel/icons/service';
import {
  ArrowRight,
  CheckInCircle,
  InfoOutline,
  MyList,
  MyListOutline,
  SpotOutline,
} from '@travel/icons/ui';
import StarRatingText from '@travel/ui/components/StarRatingText';
import Toast from '@travel/ui/components/Toast';
import { useToast } from '@travel/ui/components/Toast/hooks/useToast';
import { cx } from '@travel/utils';

import BonusProgram from './components/BonusProgram';
import Breadcrumbs from './components/Breadcrumbs/index';
import FeatureBadges from './components/FeatureBadges';
import ProviderMenuBar from './components/ProviderMenuBar';

import { PAGE_URL } from './constants';
import { appendJrdpSearchParams } from './utils';

import styles from './providerCommonHeader.module.scss';

import type { HeaderInfo, HeaderLabels, MenuPageCode, RtCond, RtCondEx } from './types';

type Props = {
  headerInfo: HeaderInfo;
  activeTab: MenuPageCode;
  activeSubTab?: string | null;
  isBookmarked: boolean;
  isBookmarkStatusFetched: boolean;
  bookmarkButtonCallback: () => void;
  isBookmarkError: boolean;
  isBookmarkLimitError: boolean;
  bookmarkErrorId: string;
  undoBookmark: () => void;
  rtCond: RtCond;
  rtCondEx: RtCondEx;
  labels: HeaderLabels;
  isPC?: boolean;
  languageCode: string;
  isJRDP?: boolean;
  jrdpQuery?: string;
  breadcrumbsHotelLabel: ReactNode;
  hasSearchBar?: boolean;
};

function FavoriteToast({
  isBookmarked,
  isBookmarkError,
  isBookmarkLimitError,
  undoBookmark,
  labels,
}: {
  isBookmarked: boolean;
  isBookmarkError: boolean;
  isBookmarkLimitError: boolean;
  undoBookmark: () => void;
  labels: HeaderLabels['favorite']['toast'];
}) {
  return (
    <div className={styles.favoriteToast}>
      {isBookmarkError ? (
        <span className={styles.toastMessage}>
          <InfoOutline className={styles.infoIcon} size={20} />
          {isBookmarkLimitError ? labels.reachedLimit : labels.error}
        </span>
      ) : isBookmarked ? (
        <>
          <span className={styles.toastMessage}>
            <CheckInCircle className={styles.checkCircleIcon} size={20} />
            {labels.addFavorite}
          </span>
          <a className={styles.toastAction} href={PAGE_URL.FAVORITE_LIST_DOMESTIC}>
            {labels.seeList}
          </a>
        </>
      ) : (
        <>
          <span className={styles.toastMessage}>
            <CheckInCircle className={styles.checkCircleIcon} size={20} />
            {labels.deleteFavorite}
          </span>
          <button type="button" className={styles.toastAction} onClick={undoBookmark}>
            {labels.undo}
          </button>
        </>
      )}
    </div>
  );
}

export default function ProviderCommonHeader(props: PropsWithChildren<Props>) {
  const {
    headerInfo,
    activeTab,
    activeSubTab,
    isBookmarked,
    isBookmarkStatusFetched,
    isBookmarkError,
    isBookmarkLimitError,
    bookmarkErrorId,
    bookmarkButtonCallback,
    undoBookmark,
    labels,
    rtCond,
    rtCondEx,
    children,
    isPC = true,
    languageCode,
    isJRDP = false,
    jrdpQuery,
    breadcrumbsHotelLabel,
    hasSearchBar = false,
  } = props;
  const { hotel, review, isBonus, isHometownTaxApplicable, breadCrumbs, ...menuPages } = headerInfo;
  const { hotelNo, hotelName, hotelAddress, premiumType, sustainability } = hotel;
  const { reviewScore, reviewCount } = review;
  const { dated } = rtCondEx;
  const { toast, showToast, hideToast } = useToast();
  const previousIsBookmarkedRef = useRef<boolean | null>(null);

  const reviewCountString = getL10nNumber(languageCode, reviewCount);

  useEffect(() => {
    if (isBookmarkStatusFetched && previousIsBookmarkedRef.current === null) {
      previousIsBookmarkedRef.current = isBookmarked;
    }
  }, [isBookmarkStatusFetched, isBookmarked]);

  useEffect(() => {
    if (previousIsBookmarkedRef.current === null) {
      return;
    }

    if (previousIsBookmarkedRef.current !== isBookmarked || bookmarkErrorId) {
      previousIsBookmarkedRef.current = isBookmarked;
      showToast(
        <FavoriteToast
          labels={labels.favorite.toast}
          isBookmarked={isBookmarked}
          isBookmarkError={isBookmarkError}
          isBookmarkLimitError={isBookmarkLimitError}
          undoBookmark={undoBookmark}
        />,
      );
    }
  }, [
    isBookmarked,
    labels.favorite.toast,
    isBookmarkError,
    undoBookmark,
    bookmarkErrorId,
    showToast,
    isBookmarkLimitError,
  ]);

  return (
    <>
      {!isJRDP && (
        <Breadcrumbs
          hotelName={hotelName}
          breadcrumbs={breadCrumbs}
          activeTab={activeTab}
          labels={labels.breadcrumbs}
          nationalLabel={labels.national}
          isDated={dated}
          breadcrumbsHotelLabel={breadcrumbsHotelLabel}
          isPC={isPC}
        />
      )}
      {/* Hotel Basic Info */}
      <div className={styles.hotelInfoWrapper}>
        {/* Hotel Information */}
        <div className={styles.hotelInfo}>
          {isBonus && <BonusProgram labels={labels.bonusTooltip} isPC={isPC} />}
          <h2 className={styles.hotelName}>
            <a
              href={appendJrdpSearchParams(
                PAGE_URL.PROVIDER_TOP(hotelNo, isJRDP),
                isJRDP,
                jrdpQuery,
              )}
              className={styles.hotelTopLink}
            >
              {hotelName}
            </a>
          </h2>
          <div className={styles.hotelInfoAndLinks}>
            {reviewCount > 0 && reviewScore > 0 && (
              <span className={styles.inlineFlexCenter}>
                <a href={appendJrdpSearchParams(PAGE_URL.REVIEW(hotelNo), isJRDP, jrdpQuery)}>
                  <StarRatingText
                    scoreClassName={styles.ratingNumber}
                    ratingValue={reviewScore}
                    isDisplayRating={false}
                    ratingColor="red"
                    iconSize={24}
                    text={
                      <span className={styles.inlineFlexCenter}>
                        <span className={styles.reviewSectionRating}>{reviewScore.toFixed(2)}</span>
                        <span className={styles.reviewPageLink}>
                          ({reviewCountString}
                          {labels.reviewCount})
                          <ArrowRight size={12} className={styles.reviewPageLinkArrow} />
                        </span>
                      </span>
                    }
                    textClassName={styles.inlineFlexCenter}
                  />
                </a>
              </span>
            )}
            <span className={styles.linkWrapper}>
              <a
                className={styles.link}
                href={appendJrdpSearchParams(PAGE_URL.MAP(hotelNo, isJRDP), isJRDP, jrdpQuery)}
              >
                <SpotOutline size={16} />
                <span className={styles.textOverflow}>〒{hotelAddress}</span>
              </a>
            </span>
            {!!sustainability.sustainableLink && (
              <span className={styles.linkWrapper}>
                <a className={styles.link} href={sustainability.sustainableLink}>
                  <Sustainability size={16} color="cilantro" />
                  {labels.sustainableLink}
                </a>
              </span>
            )}
          </div>
        </div>

        {/* Feature Badges */}
        <div className={styles.badges}>
          <FeatureBadges
            premiumType={premiumType}
            sustainability={sustainability}
            isHometownTaxApplicable={!isJRDP && isHometownTaxApplicable}
            badges={labels.badges}
          />
        </div>

        {/* Hotel Actions */}
        <div className={styles.hotelActionsWrapper}>
          {/* Release 2 */}
          {/* <div className={styles.membershipBenefits}>Membership Benefits</div> */}
          <div className={styles.hotelActions}>
            {/* Release 2 */}
            {/* {isPC && (
              <button className={styles.actionButton}>
                <div className={styles.iconWrapper}>
                  <MailOutline className={styles.mailMagazineIcon} />
                </div>
                <span className={styles.buttonLabel}>
                  {labels.newsLetter.title}
                  {labels.newsLetter.subscribe}
                </span>
              </button>
            )} */}
            <button
              className={cx(styles.actionButton, isBookmarked && styles.favorited)}
              onClick={bookmarkButtonCallback}
              aria-label={isBookmarked ? 'Added' : 'Add'}
            >
              <div className={styles.iconWrapper}>
                {isBookmarked ? (
                  <MyList className={styles.favoriteListIcon} />
                ) : (
                  <MyListOutline className={styles.favoriteListIcon} />
                )}
              </div>
              <span className={styles.buttonLabel}>
                {labels.favorite.title}
                {isBookmarked ? labels.favorite.added : labels.favorite.add}
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Tab List */}
      <ProviderMenuBar
        hotelNo={hotelNo}
        activeTab={activeTab}
        activeSubTab={activeSubTab}
        rtCond={rtCond}
        labels={labels.tabs}
        reviewCount={reviewCountString}
        isPC={isPC}
        isJRDP={isJRDP}
        jrdpQuery={jrdpQuery}
        hasSearchBar={hasSearchBar}
        {...menuPages}
      />
      {children}
      <Toast
        key={toast.key}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}
